/**
 * Forwarded-message wire format, shared by web + React Native.
 *
 * A forward is encoded into the message body (no server schema change in v1):
 *
 *   ↪ Переслано · <author> из <channel>
 *   > <original line 1>
 *   > <original line 2>
 *   <optional trailing comment by the forwarder>
 *
 * The header is a FIXED Russian wire token (not display text) so the parser can
 * round-trip it regardless of the UI language; the renderer re-localizes it into
 * a "forwarded from" pill. Clients that don't understand forwards still read the
 * markdown fallback cleanly.
 */

export interface ForwardedHeader {
  authorName: string;
  /** Source channel display name. May be empty if the original lacked one. */
  channelLabel: string;
  /** Original author's user id, snapshotted at forward time. Seeds the generated
   *  avatar colour so a photo-less author keeps the SAME orb everywhere. */
  authorId?: string;
  /** Original author's avatar URL, snapshotted at forward time. Optional — older
   *  forwards and authors without a photo won't carry one (render initials). */
  authorAvatarUrl?: string;
  /** The quoted body (without "> " prefixes), the original text. */
  quotedBody: string;
  /** Anything the forwarder typed AFTER the quoted block (their own comment). */
  trailing: string;
}

// Optional metadata tokens `⟨u:ID⟩` (author id) and `⟨a:URL⟩` (avatar) ride at
// the end of the header line, in that order. The ⟨⟩ guillemets never occur in
// names/channels/ids/URLs, so author/channel exclude `⟨` to keep the tokens
// cleanly separable; both groups are optional so older forwards parse unchanged.
const HEADER_RE =
  /^↪\s+Переслано\s+·\s+([^\n⟨]+?)(?:\s+из\s+([^\n⟨]+?))?(?:\s+⟨u:([^⟩\n]+)⟩)?(?:\s+⟨a:([^⟩\n]+)⟩)?\n((?:>[^\n]*\n?)+)([\s\S]*)$/;

/**
 * Parse forward metadata from a message body, or null if it isn't a forward.
 * Permissive on trailing text — anything after the quoted block becomes the
 * forwarder's own comment; blank → a pure forward.
 */
export function parseForwarded(content: string): ForwardedHeader | null {
  const match = HEADER_RE.exec(content);
  if (!match) return null;
  const [, authorName, channelLabel, authorId, avatarUrl, quoteBlock, trailing] = match;
  if (!quoteBlock) return null;
  // Strip a leading "> " (or ">" with no space) from each line so the inner
  // text reads as plain again.
  const quotedBody = quoteBlock
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line) => line.replace(/^>\s?/, ''))
    .join('\n')
    .trim();
  const avatar = (avatarUrl ?? '').trim();
  const id = (authorId ?? '').trim();
  return {
    authorName: (authorName ?? '').trim(),
    channelLabel: (channelLabel ?? '').trim(),
    ...(id ? { authorId: id } : {}),
    ...(avatar ? { authorAvatarUrl: avatar } : {}),
    quotedBody,
    trailing: (trailing ?? '').trim(),
  };
}

/**
 * Build the wire body for forwarding `content` authored by `authorName` from
 * `sourceChannelLabel`. `someoneFallback` is used when the author is unknown
 * (the caller passes a localized "someone"). Round-trips through
 * {@link parseForwarded}.
 */
export function buildForwardBody(
  input: {
    authorName: string;
    sourceChannelLabel: string;
    content: string;
    authorId?: string | null;
    authorAvatarUrl?: string | null;
  },
  someoneFallback = 'кто-то',
): string {
  const author = input.authorName.trim() || someoneFallback;
  const channel = input.sourceChannelLabel.trim();
  const id = input.authorId?.trim();
  const avatar = input.authorAvatarUrl?.trim();
  let header = channel ? `↪ Переслано · ${author} из ${channel}` : `↪ Переслано · ${author}`;
  // Metadata tokens sit last (id then avatar) so they never interfere with the
  // author/channel parse.
  if (id) header += ` ⟨u:${id}⟩`;
  if (avatar) header += ` ⟨a:${avatar}⟩`;
  const quoted = input.content
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
  return `${header}\n${quoted}`.trim();
}
